/**
 * FileBrowserTab Component
 * Main file browser interface with folders and files
 */

import React, { useState } from 'react';
import { Stack, Group } from '@/components/ui/layout';
import { Button } from '@/components/ui/core';
import { ActionIcon } from '@/components/ui';
import { SegmentedControl } from '@mantine/core';
import { TextInput } from '@/components/ui/inputs';
import { ConfirmDeleteModal } from '@/components/ui/overlays';
import { IconPlus, IconSearch, IconLayoutGrid, IconList, IconRefresh } from '@tabler/icons-react';
import { FolderBreadcrumb } from './FolderBreadcrumb';
import { FileGridView } from './FileGridView';
import { FileTableView } from './FileTableView';
import { BulkActionsToolbar } from './BulkActionsToolbar';
import { CreateFolderModal } from './CreateFolderModal';
import { useFileManagement } from '../hooks/useFileManagement';
import { useFolderManagement } from '../hooks/useFolderManagement';
import { useS3Operations } from '../hooks/useS3Operations';
import { FileRecordModelView, FolderInfo, ViewMode } from '../types';

interface FileBrowserTabProps {
  domain?: string;
  recordId?: string;
  onFilePreview?: (file: FileRecordModelView) => void;
  onFileDetails?: (file: FileRecordModelView) => void;
}

export const FileBrowserTab: React.FC<FileBrowserTabProps> = ({
  domain,
  recordId,
  onFilePreview,
  onFileDetails,
}) => {
  const fileManagement = useFileManagement('/', domain, recordId);
  const folderManagement = useFolderManagement(domain, recordId, fileManagement.navigation.currentPath);
  const s3Operations = useS3Operations();

  const [createFolderOpened, setCreateFolderOpened] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteModalOpened, setDeleteModalOpened] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<string | null>(null);
  const [bulkDeleteModalOpened, setBulkDeleteModalOpened] = useState(false);

  // Handlers
  const handleFolderClick = (folder: FolderInfo) => {
    fileManagement.navigateToFolder(folder.path);
  };

  const handleFileClick = (file: FileRecordModelView) => {
    if (onFileDetails) {
      onFileDetails(file);
    }
  };

  const handlePreview = (file: FileRecordModelView) => {
    if (onFilePreview) {
      onFilePreview(file);
    }
  };

  const handleDownload = async (fileId: string, fileName: string) => {
    await s3Operations.downloadFile(fileId, fileName);
  };

  const handleDelete = (fileId: string) => {
    setFileToDelete(fileId);
    setDeleteModalOpened(true);
  };

  const confirmDelete = () => {
    if (fileToDelete) {
      fileManagement.deleteFile(fileToDelete);
      setDeleteModalOpened(false);
      setFileToDelete(null);
    }
  };

  const handleBulkDelete = () => {
    setBulkDeleteModalOpened(true);
  };

  const confirmBulkDelete = () => {
    fileManagement.bulkDelete(Array.from(fileManagement.selectedFiles.fileIds));
    setBulkDeleteModalOpened(false);
  };

  const handleBulkDownload = () => {
    s3Operations.bulkDownload(Array.from(fileManagement.selectedFiles.fileIds));
  };

  const handleBulkMove = () => {
    // TODO: Implement move modal
    console.log('Bulk move not yet implemented');
  };

  const handleSearch = () => {
    fileManagement.searchFiles(searchQuery);
  };

  return (
    <Stack gap="md">
      {/* Breadcrumb and Actions */}
      <Group justify="space-between">
        <FolderBreadcrumb
          breadcrumbs={fileManagement.navigation.breadcrumbs}
          onNavigate={fileManagement.navigateToFolder}
          canGoBack={fileManagement.navigation.historyIndex > 0}
          canGoForward={fileManagement.navigation.historyIndex < fileManagement.navigation.history.length - 1}
          onGoBack={fileManagement.navigateBack}
          onGoForward={fileManagement.navigateForward}
        />

        <Group gap="xs">
          <ActionIcon
            variant="subtle"
            onClick={fileManagement.refreshFiles}
            loading={fileManagement.loading}
            title="Refresh"
          >
            <IconRefresh size={18} />
          </ActionIcon>
          <SegmentedControl
            value={fileManagement.viewMode}
            onChange={(value) => fileManagement.toggleViewMode()}
            data={[
              { label: <IconLayoutGrid size={16} />, value: 'grid' },
              { label: <IconList size={16} />, value: 'table' },
            ]}
          />
        </Group>
      </Group>

      {/* Toolbar */}
      <Group>
        <Button
          leftSection={<IconPlus size={16} />}
          size="sm"
          onClick={() => setCreateFolderOpened(true)}
        >
          New Folder
        </Button>

        <TextInput
          placeholder="Search files..."
          leftSection={<IconSearch size={16} />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.currentTarget.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          style={{ flex: 1 }}
        />
      </Group>

      {/* Bulk Actions */}
      <BulkActionsToolbar
        selectedFiles={fileManagement.selectedFiles}
        onBulkDelete={handleBulkDelete}
        onBulkMove={handleBulkMove}
        onBulkDownload={handleBulkDownload}
        onClearSelection={fileManagement.deselectAllFiles}
      />

      {/* File/Folder View */}
      {fileManagement.viewMode === 'grid' ? (
        <FileGridView
          files={fileManagement.files}
          folders={folderManagement.folders}
          selectedFileIds={fileManagement.selectedFiles.fileIds}
          onFileSelect={fileManagement.selectFile}
          onFileClick={handleFileClick}
          onFolderClick={handleFolderClick}
          onDownload={handleDownload}
          onPreview={handlePreview}
          onDelete={handleDelete}
          loading={fileManagement.loading || folderManagement.loading}
        />
      ) : (
        <FileTableView
          files={fileManagement.files}
          folders={folderManagement.folders}
          selectedFileIds={fileManagement.selectedFiles.fileIds}
          onFileSelect={fileManagement.selectFile}
          onSelectAll={fileManagement.selectAllFiles}
          onFileClick={handleFileClick}
          onFolderClick={handleFolderClick}
          onDownload={handleDownload}
          onPreview={handlePreview}
          onDelete={handleDelete}
        />
      )}

      {/* Create Folder Modal */}
      <CreateFolderModal
        opened={createFolderOpened}
        onClose={() => setCreateFolderOpened(false)}
        onCreate={async (data) => {
          await folderManagement.createFolder(data);
        }}
        parentPath={fileManagement.navigation.currentPath}
        domain={domain}
        recordId={recordId}
      />

      {/* Delete File Modal */}
      <ConfirmDeleteModal
        opened={deleteModalOpened}
        onClose={() => setDeleteModalOpened(false)}
        onConfirm={confirmDelete}
        title="Delete File"
        message="Are you sure you want to delete this file? This action cannot be undone."
        gradientVariant="danger"
      />

      {/* Bulk Delete Modal */}
      <ConfirmDeleteModal
        opened={bulkDeleteModalOpened}
        onClose={() => setBulkDeleteModalOpened(false)}
        onConfirm={confirmBulkDelete}
        title="Delete Files"
        message="Are you sure you want to delete the selected files? This action cannot be undone."
        itemCount={fileManagement.selectedFiles.fileIds.size}
        gradientVariant="danger"
      />
    </Stack>
  );
};
